import { User } from '@sr/types';
import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useUser } from '../../hooks/use-user';
import {
  ArrowLeftIcon,
  Box,
  Button,
  CreditCardIcon,
  Grid,
  Link,
  LogoutIcon,
  Text,
} from '../../ui';
import API from '../../utils/api';
import apiRequest from '../../utils/api-request';
import HelpButton from '../help/HelpButton';
import ChangeNameModal from './ChangeNameModal';
import ChangePasswordModal from './ChangePasswordModal';
import DeleteAccountModal from './DeleteAccountModal';
import ProOptions from './ProOptions';

async function handleManageClick(
  user: User,
  setLoading: (arg0: boolean) => void
) {
  try {
    setLoading(true);
    const { session } = (await apiRequest('stripe/portal-session', {
      method: 'POST',
      json: { returnUrl: window.location.href },
    }).json()) as { session: { url: string } };
    window.location.assign(session.url);
  } catch (error) {
    console.log('Error creating billing session', error);
    window.alert(
      'Something went wrong. Please try again or email hello@standardresume.co'
    );
    setLoading(false);
  }
}

const AccountSettings = () => {
  const user = useUser();
  const [isBillingLoading, setIsBillingLoading] = useState(false);

  if (!user) return null;
  if (user.isAnonymous) return <Redirect to="/resumes" />;

  return (
    <Box>
      <Grid variant="accountPage" py={4}>
        <Box>
          <Link to="/resumes">
            <ArrowLeftIcon />
          </Link>
        </Box>
        <Text
          sx={{
            variant: 'text.baseEmphasis',
            lineHeight: '32px',
          }}
        >
          Account & Security
        </Text>
      </Grid>
      <Grid variant="accountPage">
        <Box />
        <Box>
          <Box mb={5}>
            <Text
              sx={{
                variant: 'text.base',
                color: 'black50',
                lineHeight: '32px',
              }}
            >
              Name
            </Text>
            <Text variant="title" id="gi-account-name">
              {`${user.firstName || ''} ${user.lastName || ''}`}
            </Text>
            <ChangeNameModal user={user} />
          </Box>

          <Box mb={5}>
            <Text
              sx={{
                variant: 'text.base',
                color: 'black50',
                lineHeight: '32px',
              }}
            >
              Email
            </Text>
            <Text variant="title" id="gi-account-email">
              {user.email}
            </Text>
          </Box>

          <Box mb="40px">
            <Text
              sx={{
                variant: 'text.base',
                color: 'black50',
                lineHeight: '32px',
              }}
            >
              Password
            </Text>
            <ChangePasswordModal user={user} />
          </Box>

          <ProOptions user={user} />

          <Button
            onClick={() => API.auth.signOut({ userId: user.id })}
            sx={{
              variant: 'buttons.link',
              pl: 0,
              color: 'black',
            }}
          >
            <LogoutIcon mr={2} /> Log Out
          </Button>
          {user.stripeCustomerId && (
            <Button
              onClick={() => handleManageClick(user, setIsBillingLoading)}
              disabled={isBillingLoading}
              sx={{
                variant: 'buttons.link',
                pl: 0,
                color: 'black',
              }}
            >
              <CreditCardIcon mr={2} />{' '}
              {isBillingLoading ? 'Loading...' : 'Manage billing'}
            </Button>
          )}
          <DeleteAccountModal user={user} />
        </Box>
        <HelpButton userId={user.id} />
      </Grid>
    </Box>
  );
};

export default AccountSettings;
